import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ApplicationSdk } from '../../../../core/sdk';
import BaseEmployeeStore from '../../../../shared/stores/baseEmployeeStore';
import { formatName } from '../../../../utils/helper';
import { MatenRequestResponse } from '../../../../shared';

export interface RequestType {
    id: number;
    name: string;
    description: string;
    icon: string;
}
export interface RequestApproval {
    id: number;
    approved: number;
    firstname: string;
    lastname: string;
    fullName: string;
    comment: string;
}
export interface RequestItem {
    id: number;
    type: RequestType;
    startDate: string;
    endDate: string;
    comment: string;
    approver?: RequestApproval;
    status: string;
    created: number;
}

const formatDate = (date: Date | null) => {
    if (!date) return null;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Monate sind 0-basiert
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
};

export class RequestEditor {
    public startDate: Date | null = null;
    public endDate: Date | null = null;
    public comment: string = '';
    public selectedType: RequestType | null = null;
    public requestTypes: RequestType[] = [];

    constructor() {
        makeObservable(this, {
            startDate: observable,
            endDate: observable,
            comment: observable,
            selectedType: observable,
            requestTypes: observable,
            setStartDate: action,
            setEndDate: action,
            setComment: action,
            setSelectedType: action,
            setRequestTypes: action,
            reset: action,
            isTypeSelectable: computed,
        });
    }

    public setStartDate(date: Date | null): void {
        this.startDate = date;
    }

    public setEndDate(date: Date | null): void {
        this.endDate = date;
    }

    public setComment(comment: string): void {
        this.comment = comment;
    }

    public setSelectedType(type: RequestType | null): void {
        this.selectedType = type;
    }

    public setRequestTypes(types: RequestType[]): void {
        this.requestTypes = types;

        // if we find only one request type, we can set it as the selected type
        if (this.requestTypes.length === 1) {
            this.selectedType = this.requestTypes[0];
        }
    }

    public reset(): void {
        this.startDate = null;
        this.endDate = null;
        this.comment = '';
        this.selectedType = null;
    }

    get isTypeSelectable(): boolean {
        return this.requestTypes.length !== 1;
    }

    get isValid(): boolean {
        return (
            this.startDate !== null &&
            this.endDate !== null &&
            this.selectedType !== null &&
            this.startDate <= this.endDate
        );
    }
}

class RequestStore extends BaseEmployeeStore {
    public requestList: RequestItem[] = [];
    public requestEditor: RequestEditor | null = null;

    constructor(sdk: ApplicationSdk) {
        super(sdk);

        makeObservable(this, {
            requestList: observable,
            requestEditor: observable,
        });
    }

    public async onInit(): Promise<void> {
        // Update menu configuration
        const menuItem = this.sdk.findMenuItemByKey('requests');

        if (menuItem) {
            this.setMenuConfig({
                customHeader: {
                    title: menuItem.label,
                    icon: menuItem.icon,
                },
            });
        }

        // update footer button
        this.updateFooterButton({
            label: 'Neuer Antrag',
            icon: 'pi pi-plus',
            onClick: () => this.openNewRequest(),
        });

        await this.fetchRequests();
    }

    public quitRequestEditor(refresh: boolean = false): void {
        this.requestEditor = null;
        if (refresh) {
            this.fetchRequests();
        }
    }

    public async openNewRequest(): Promise<void> {
        if (this.requestEditor) return;
        this.setLoading(true);

        const requestNew: any = await this.sdk.fetchApi('request', 'employee/new', {});

        this.setLoading(false);

        if (requestNew?.status === 200) {
            runInAction(() => {
                this.requestEditor = new RequestEditor();
                this.requestEditor.setRequestTypes(
                    (requestNew.json.data || []).map(
                        (item: any): RequestType => ({
                            id: item.id,
                            name: item.name,
                            icon: item.icon,
                            description: item.description,
                        })
                    )
                );
            });
        }
    }

    public async fetchRequests(): Promise<void> {
        this.setLoading(true);

        const requestListResult: any = await this.sdk.fetchApi('request', 'employee/query', {}); // Todo: If we want to have pagination, we need to pass page and limit here

        this.setLoading(false);

        runInAction(() => {
            if (requestListResult?.status === 200) {
                const { data } = requestListResult.json;

                this.requestList = (data || []).map(
                    (item: any): RequestItem => ({
                        id: item.id,
                        type: item.request_type,
                        startDate: item.start_date_formatted,
                        endDate: item.end_date_formatted,
                        comment: item.comment,
                        approver: item.approver
                            ? {
                                  id: item.approver.id,
                                  firstname: item.approver.firstname || '',
                                  lastname: item.approver.lastname || '',
                                  approved: item.approved_utc * 1000,
                                  comment: item.approver_comment || '',
                                  fullName: formatName(
                                      item.approver.firstname,
                                      item.approver.lastname
                                  ),
                              }
                            : undefined,
                        status: item.status,
                        created: item.created_utc,
                    })
                );
            }
        });
    }

    public async createRequest(): Promise<MatenRequestResponse> {
        const newRequestResult: any = await this.sdk.fetchApi('request', 'employee/add', {
            request_type_id: this.requestEditor?.selectedType?.id || null,
            start_date: formatDate(this.requestEditor?.startDate || null) || undefined,
            end_date: formatDate(this.requestEditor?.endDate || null) || undefined,
            comment: this.requestEditor?.comment || '',
        });

        if (newRequestResult?.status === 201) {
            return { success: true, status: 201 };
        } else if (newRequestResult?.status === 403) {
            return {
                success: false,
                status: 403,
                error: newRequestResult.json.error || 'ERR_UNKNOWN',
            };
        }

        return { success: false, status: 403, error: 'ERR_UNKNOWN' };
    }

    get isBackButtonVisible(): boolean {
        return true;
    }

    get isFooterButtonVisible(): boolean {
        // if (this.orderFinished) return false;
        return !this.requestEditor;
    }

    get isFooterButtonEnabled(): boolean {
        return true;
    }

    get isFooterButtonLoading(): boolean {
        // return this.orderLoading;
        return this.isLoading;
    }

    get footerButtonHelpText(): string {
        return '';
        // return this.orderable ? 'Bestellung abschließen' : 'Bitte wählen Sie ein Objekt und mindestens 1 Produkt aus';
    }

    protected async onBeforeNavigateBack(): Promise<boolean> {
        if (this.isLoading) return false;
        return true;
    }
}

export default RequestStore;
